import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Annotator from "../utils/annotator/Annotator";
import {
  readMostRecentHighlightColor,
  storeMostRecentHighlightColor,
} from "../utils/annotator/highlightColors";
import AnnotationStorageProvider from "../utils/annotator/AnnotationStorageProvider";
import AnnotationApiClient from "../utils/annotator/AnnotationApiClient";
import NoteModal from "../components/NoteModal";
import NoteButton from "../components/NoteButton";
import LiveRegion from "../components/LiveRegion";
import { useFeature } from "./FeatureFlagContext";
import {
  Annotation,
  AnnotationType,
  getAnnotationLabel,
  toBookmarkAnnotation,
  toNoteAnnotation,
} from "../utils/annotator/annotationsHelper";
import { EpubCFI } from "epubjs";
import useLiveAnnouncement from "../hooks/useLiveAnnouncement";

export const BOOKMARK_ADDED_MESSAGE = "Bookmark added";
export const BOOKMARK_REMOVED_MESSAGE = "Bookmark removed";

export const ANNOUNCEMENT_DELAY_MS = 1000;

type Rect = { left: number; right: number; top: number; bottom: number };
type GetRect = () => Rect | null | undefined;

type NoteModalMode = "create" | "edit";

interface NoteModalState {
  mode: NoteModalMode;
  note: Annotation | null;
  cfi?: string;
  text?: string;
  locator?: unknown;
  onDismiss?: (() => void) | null;
  onViewAll?: (() => void) | null;
}

interface SelectNoteButtonState {
  getViewportRect?: GetRect | null;
}

interface SelectionInfo {
  textRange: { toString: () => string };
  cfi: string;
  locator: unknown;
  onDismiss?: () => void;
}

const isSelectionWhitespace = (selection: SelectionInfo) => {
  return selection.textRange.toString().trim().length === 0;
};

interface SelectionProviderOptions {
  pauseTts?: boolean;
}

type SelectionProvider = (
  options?: SelectionProviderOptions,
) => SelectionInfo | null | undefined;

interface AnnotationsContextValue {
  bookmarks: Annotation[];
  notes: Annotation[];
  noteModal: NoteModalState | null;
  selectNoteButton: SelectNoteButtonState | null;
  removeBookmark: (location: string) => void;
  removeNote: (location: string) => void;
  initializeAnnotations: () => Promise<void>;
  addBookmark: (cfi: string, text: string, locator: unknown) => void;
  addNote: (
    location: string,
    text: string,
    color: string,
    note: string | null,
    locator: unknown,
  ) => void;
  openNoteModal: (
    mode: NoteModalMode,
    note: Annotation | null,
    payload: Omit<NoteModalState, "mode" | "note">,
  ) => void;
  closeNoteModal: () => void;
  openSelectNoteButton: (payload: SelectNoteButtonState) => void;
  closeSelectNoteButton: () => void;
  registerViewAllNotes: (fn: () => void) => void;
  registerSelectionProvider: (fn: SelectionProvider) => void;
  handleCreateNote: () => void;
  handleCreateHighlight: (colorValue?: string) => void;
  handleEditNote: (note: Annotation, showViewAll?: boolean) => void;
  saveNoteEdit: (location: string, note: string | null, color: string) => void;
  handleDisplayReset: () => void;
  setNoteButtonsBlocked: (blocked: boolean) => void;
  isNoteBeingEntered: () => boolean;
  getAnnotationsSortedByCfi: (type: AnnotationType | undefined) => Annotation[];
  annotationAnnouncing: boolean;
  annotationAnnouncementMessage: string;
  setModalLiveRegionActive: (active: boolean) => void;
}

const AnnotationsContext = createContext<AnnotationsContextValue>(
  null as unknown as AnnotationsContextValue,
);

interface AnnotationsProviderProps {
  bookshareId: string;
  isRnib: boolean;
  children: ReactNode;
}

export function AnnotationsProvider({
  bookshareId,
  isRnib,
  children,
}: AnnotationsProviderProps) {
  const HIGHLIGHTS_ENABLED = useFeature("annotations");
  // Annotator is a plain JS class; treat as any to avoid leaking inaccurate JSDoc types.
  const annotatorRef = useRef<any>(null);
  const onViewAllNotesRef = useRef<(() => void) | null>(null);
  const selectionProviderRef = useRef<SelectionProvider | null>(null);
  if (!annotatorRef.current) {
    // Cast to bypass inaccurate JSDoc-inferred types on the underlying JS classes.
    const annotator: any = new (Annotator as any)(parseInt(bookshareId));
    annotator.storageProvider = new (AnnotationStorageProvider as any)();
    annotator.apiClient = isRnib ? null : new (AnnotationApiClient as any)();
    annotatorRef.current = annotator;
  }

  const [bookmarks, setBookmarks] = useState<Annotation[]>([]);
  const [notes, setNotes] = useState<Annotation[]>([]);
  const [noteModal, setNoteModal] = useState<NoteModalState | null>(null);
  const [selectNoteButton, setSelectNoteButton] =
    useState<SelectNoteButtonState | null>(null);
  const [noteButtonsBlocked, setNoteButtonsBlocked] = useState(false);
  const [annotationAnnouncing, announceAnnotation] = useLiveAnnouncement();
  const [annotationAnnouncementMessage, setAnnotationAnnouncementMessage] =
    useState("");

  const announcementDelayRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  useEffect(() => () => clearTimeout(announcementDelayRef.current), []);

  const [modalLiveRegionActive, setModalLiveRegionActive] = useState(false);

  async function initializeAnnotations() {
    const [bookmarks, notes] = await Promise.all([
      annotatorRef.current!.initializeBookmarks(),
      annotatorRef.current!.initializeNotes(),
    ]);
    setBookmarks(bookmarks.map(toBookmarkAnnotation));
    setNotes(notes.map(toNoteAnnotation));
  }

  function registerViewAllNotes(fn: () => void) {
    onViewAllNotesRef.current = fn;
  }

  function registerSelectionProvider(fn: SelectionProvider) {
    selectionProviderRef.current = fn;
  }

  function addBookmark(cfi: string, text: string, locator: unknown) {
    annotatorRef.current!.addBookmark(cfi, text, locator);
    setBookmarks(
      annotatorRef.current!.listActiveBookmarks().map(toBookmarkAnnotation),
    );
    setAnnotationAnnouncement(BOOKMARK_ADDED_MESSAGE);
  }

  function removeBookmark(location: string) {
    if (annotatorRef.current!.removeBookmark(location)) {
      setBookmarks(
        annotatorRef.current!.listActiveBookmarks().map(toBookmarkAnnotation),
      );
      setAnnotationAnnouncement(BOOKMARK_REMOVED_MESSAGE);
    }
  }

  const getAnnotationTypeLabel = (annotation: any) =>
    getAnnotationLabel(toNoteAnnotation(annotation).type);

  function addNote(
    location: string,
    text: string,
    color: string,
    note: string | null,
    locator: unknown,
  ) {
    const addedAnnotation = annotatorRef.current!.addNote(
      location,
      text,
      color,
      note,
      locator,
    );
    setNotes(annotatorRef.current!.listActiveNotes().map(toNoteAnnotation));
    setAnnotationAnnouncement(
      `${getAnnotationTypeLabel(addedAnnotation)} added`,
    );
  }

  function removeNote(location: string) {
    const removedAnnotation = annotatorRef.current!.removeNote(location);
    if (removedAnnotation) {
      setNotes(annotatorRef.current!.listActiveNotes().map(toNoteAnnotation));
      setAnnotationAnnouncement(
        `${getAnnotationTypeLabel(removedAnnotation)} removed`,
      );
    }
  }

  function updateNote(
    location: string,
    newNote: string | null,
    newColor?: string,
  ) {
    const updatedAnnotation = annotatorRef.current!.editNote(
      location,
      newNote,
      newColor,
    );
    if (updatedAnnotation) {
      setNotes(annotatorRef.current!.listActiveNotes().map(toNoteAnnotation));
      setAnnotationAnnouncement(
        `${getAnnotationTypeLabel(updatedAnnotation)} updated`,
      );
    }
  }

  function saveNoteEdit(location: string, note: string | null, color: string) {
    updateNote(location, note, color);
    storeMostRecentHighlightColor(color);
  }

  function openNoteModal(
    mode: NoteModalMode,
    note: Annotation | null,
    payload: Omit<NoteModalState, "mode" | "note">,
  ) {
    setSelectNoteButton(null);
    setNoteModal({ mode, note, ...payload });
  }

  function closeNoteModal() {
    setNoteModal(null);
  }

  function openSelectNoteButton(payload: SelectNoteButtonState) {
    setSelectNoteButton(payload);
  }

  function closeSelectNoteButton() {
    setSelectNoteButton(null);
  }

  function handleNoteSave(note: string | null, colorValue: string) {
    if (!noteModal) return;
    const { cfi, text, locator, onDismiss } = noteModal;
    addNote(cfi!, text!, colorValue, note, locator);
    storeMostRecentHighlightColor(colorValue);
    setNoteModal(null);
    onDismiss?.();
  }

  function handleNoteCancel() {
    if (!noteModal) return;
    const { onDismiss } = noteModal;
    setNoteModal(null);
    onDismiss?.();
  }

  function handleCreateHighlight(
    colorValue: string = readMostRecentHighlightColor(),
  ) {
    if (!HIGHLIGHTS_ENABLED) return;
    const selection = selectionProviderRef.current?.({ pauseTts: false });
    if (!selection || isSelectionWhitespace(selection)) return;
    const { textRange, cfi, locator, onDismiss } = selection;
    addNote(cfi, textRange.toString(), colorValue, null, locator);
    storeMostRecentHighlightColor(colorValue);
    onDismiss?.();
  }

  function handleCreateNote() {
    if (!HIGHLIGHTS_ENABLED) return;
    const selection = selectionProviderRef.current?.();
    if (!selection || isSelectionWhitespace(selection)) return;
    const { textRange, cfi, locator, onDismiss } = selection;
    closeSelectNoteButton();
    openNoteModal("create", null, {
      cfi,
      text: textRange.toString(),
      locator,
      onDismiss,
    });
  }

  function handleEditNote(note: Annotation, showViewAll: boolean = true) {
    openNoteModal("edit", note, {
      ...(showViewAll && {
        onViewAll: () => {
          closeNoteModal();
          onViewAllNotesRef?.current?.();
        },
      }),
    });
  }

  function handleDisplayReset() {
    closeNoteModal();
    closeSelectNoteButton();
  }

  function handleDeleteFromEditModal(location: string) {
    removeNote(location);
    closeNoteModal();
  }

  const cfiComparator = useMemo(() => new EpubCFI(), []);

  const getAnnotationsSortedByCfi = useCallback(
    (type?: AnnotationType) => {
      let annotations = [...bookmarks, ...notes];
      if (type) annotations = annotations.filter((a) => a.type === type);
      return annotations.sort((a, b) =>
        cfiComparator.compare(a.location, b.location),
      );
    },
    [bookmarks, notes, cfiComparator],
  );

  function setAnnotationAnnouncement(announcement: string) {
    clearTimeout(announcementDelayRef.current);
    announcementDelayRef.current = setTimeout(() => {
      setAnnotationAnnouncementMessage(announcement);
      announceAnnotation();
    }, ANNOUNCEMENT_DELAY_MS);
  }

  return (
    <AnnotationsContext.Provider
      value={{
        bookmarks,
        notes,
        noteModal,
        selectNoteButton,
        removeBookmark,
        removeNote,
        initializeAnnotations,
        addBookmark,
        addNote,
        openNoteModal,
        openSelectNoteButton,
        closeSelectNoteButton,
        registerViewAllNotes,
        registerSelectionProvider,
        handleCreateNote,
        closeNoteModal,
        handleCreateHighlight,
        handleEditNote,
        saveNoteEdit,
        handleDisplayReset,
        setNoteButtonsBlocked,
        isNoteBeingEntered: () =>
          noteModal?.mode === "create" || noteModal?.mode === "edit",
        getAnnotationsSortedByCfi,
        annotationAnnouncing,
        annotationAnnouncementMessage,
        setModalLiveRegionActive,
      }}
    >
      {children}
      <LiveRegion
        /* We only want to make live region announcements from this context 
           if no modal is also making annotations announcements - otherwise
           NVDA will see both live regions and read announcements twice */
        announcing={annotationAnnouncing && !modalLiveRegionActive}
        message={annotationAnnouncementMessage}
      />
      {HIGHLIGHTS_ENABLED && (
        <>
          {noteModal?.mode === "create" && (
            <NoteModal
              mode="create"
              onSave={handleNoteSave}
              onCancel={handleNoteCancel}
              annotatedText={noteModal.text}
            />
          )}
          {noteModal?.mode === "edit" && (
            <NoteModal
              mode="edit"
              annotatedText={noteModal.text ?? noteModal.note?.text}
              annotation={noteModal.note ?? undefined}
              onEdit={(newNote: string | null, newColorValue: string) => {
                saveNoteEdit(noteModal.note!.location, newNote, newColorValue);
                closeNoteModal();
              }}
              onDelete={() =>
                handleDeleteFromEditModal(noteModal.note!.location)
              }
              onViewAll={noteModal.onViewAll ?? undefined}
              onCancel={closeNoteModal}
            />
          )}
          {!noteButtonsBlocked && selectNoteButton != null && (
            <NoteButton
              onHighlightColorSelected={handleCreateHighlight}
              onCreateNote={handleCreateNote}
            />
          )}
        </>
      )}
    </AnnotationsContext.Provider>
  );
}

export function useAnnotations(): AnnotationsContextValue {
  const context = useContext(AnnotationsContext);
  if (!context) {
    throw new Error(
      "useAnnotations must be used within an AnnotationsProvider",
    );
  }
  return context;
}

export default AnnotationsContext;
export type { AnnotationsContextValue };
